import { useContext, useMemo } from 'react';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxControlSurface,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from '@utilities-one/gpm-ui';

import { FormBase } from '@/library/form/components/FormBase';
import { useFieldContext } from '@/library/form/contexts';
import { FormControlProps, SelectOption } from '@/library/form/types/form.type';
import { OptionalPortalContainerContext } from '@/library/portal/OptionalPortalContainerContext';

type FormMultiSelectProps = FormControlProps & {
  options: SelectOption[];
  placeholder?: string;
  emptyText?: string;
  disabledOptions?: Array<{ value: string }>;
};

/** Multi-value select on a `string[]` field — Combobox chips (Radix Select is single-value only). */
export function FormMultiSelect({
  options,
  placeholder = 'Select…',
  emptyText = 'No results',
  disabledOptions,
  ...props
}: FormMultiSelectProps) {
  const field = useFieldContext<string[]>();
  const portalHostEl = useContext(OptionalPortalContainerContext);
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
  const selected = field.state.value ?? [];

  const disabledSet = useMemo(() => new Set(disabledOptions?.map((option) => option.value)), [disabledOptions]);

  const optionsByValue = useMemo(() => {
    const map: Record<string, SelectOption> = {};
    for (const option of options) map[option.value] = option;
    return map;
  }, [options]);

  const selectedItems = useMemo<SelectOption[]>(
    () => selected.map((value) => optionsByValue[value] ?? { value, label: value }),
    [selected, optionsByValue]
  );

  return (
    <FormBase {...props}>
      <Combobox
        multiple
        items={options}
        value={selectedItems}
        itemToStringValue={(item) => (item as SelectOption)?.label ?? ''}
        isItemEqualToValue={(a, b) => (a as SelectOption | null)?.value === (b as SelectOption | null)?.value}
        onValueChange={(next) => field.handleChange(((next ?? []) as SelectOption[]).map((option) => option.value))}
      >
        <ComboboxControlSurface>
          <ComboboxChips className="w-full" aria-invalid={isInvalid} onBlur={field.handleBlur}>
            <ComboboxValue>
              {(vals: SelectOption[]) =>
                vals.map((entry) => (
                  <ComboboxChip key={entry.value}>
                    {entry.label}
                  </ComboboxChip>
                ))
              }
            </ComboboxValue>
            <ComboboxChipsInput id={field.name} name={field.name} placeholder={selected.length ? '' : placeholder} />
          </ComboboxChips>
        </ComboboxControlSurface>
        <ComboboxContent portalContainer={portalHostEl ?? undefined}>
          <ComboboxList>
            {(item: SelectOption) => (
              <ComboboxItem key={item.value} value={item} disabled={disabledSet.has(item.value)}>
                {item.label}
              </ComboboxItem>
            )}
          </ComboboxList>
          <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        </ComboboxContent>
      </Combobox>
    </FormBase>
  );
}
